import { useCallback, useState } from 'react'

import { CartLineItem, FrequencyOption } from '../../../services/cartService/cart.service.types'

export interface UseFrequencyModalState {
  isOpen: boolean
  lineItem: CartLineItem | null
  selectedPlanId: number | null
}

export interface UseFrequencyModalResult {
  isOpen: boolean
  lineItem: CartLineItem | null
  selectedPlanId: number | null
  selectedFrequency: string
  availableFrequencies: FrequencyOption[]
  openModal: (lineItem: CartLineItem) => void
  closeModal: () => void
  selectFrequency: (planId: number | string) => void
  resetSelection: () => void
}

const initialState: UseFrequencyModalState = {
  isOpen: false,
  lineItem: null,
  selectedPlanId: null,
}

export const useFrequencyModal = (): UseFrequencyModalResult => {
  const [state, setState] = useState<UseFrequencyModalState>(initialState)

  const openModal = useCallback((lineItem: CartLineItem) => {
    const currentPlanId = lineItem.subscriptionDetail?.planId ?? null
    setState({
      isOpen: true,
      lineItem,
      selectedPlanId: currentPlanId,
    })
  }, [])

  const closeModal = useCallback(() => {
    setState(initialState)
  }, [])

  const selectFrequency = useCallback((planId: number | string) => {
    setState(prev => ({
      ...prev,
      selectedPlanId: Number(planId),
    }))
  }, [])

  const resetSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedPlanId: prev.lineItem?.subscriptionDetail?.planId ?? null,
    }))
  }, [])

  const availableFrequencies: FrequencyOption[] =
    state.lineItem?.variantMetaData?.plans?.map(plan => ({
      planId: plan.id,
      label: plan.frequency.displayLabel,
      discountPercent: plan.benefitValue,
      isDefault: plan.isDefault,
    })) ?? []

  const selectedFrequency = availableFrequencies.find(f => f.planId === state.selectedPlanId)?.label ?? ''

  return {
    isOpen: state.isOpen,
    lineItem: state.lineItem,
    selectedPlanId: state.selectedPlanId,
    selectedFrequency,
    availableFrequencies,
    openModal,
    closeModal,
    selectFrequency,
    resetSelection,
  }
}
